import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getSpeakingFeedbackById, updateSpeakingFeedback } from '../../api/speakingFeedbackApi';
import {
    Typography,
    Box,
    Card,
    CardContent,
    Grid,
    Avatar,
    Chip,
    CircularProgress,
    Paper,
    Link,
    Divider,
} from '@mui/material';
import {
    Feedback as FeedbackIcon,
    Person as PersonIcon,
    Schedule as ScheduleIcon,
    Edit as EditIcon,
    VideoCall as VideoCallIcon,
    CalendarToday as CalendarIcon,
    AccessTime as TimeIcon,
    Language as LanguageIcon,
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import ModernButton from '../../components/common/ModernButton';
import ModernTextField from '../../components/common/ModernTextField';

const SpeakingFeedbackForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [sessionData, setSessionData] = useState(null);
    const [feedbackData, setFeedbackData] = useState({
        instructorFeedback: '',
        instructorAdvice: ''
    });

    useEffect(() => {
        const fetchSessionData = async () => {
            try {
                setLoading(true);
                const response = await getSpeakingFeedbackById(id);
                console.log("Session data response:", response);
                
                const data = response.data || response;
                setSessionData(data);
                
                // Initialize feedback data
                setFeedbackData({
                    instructorFeedback: data.instructorFeedback || '',
                    instructorAdvice: data.instructorAdvice || ''
                });
            } catch (error) {
                console.error('Failed to fetch session data:', error);
                showToast('Failed to load session data', 'error');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchSessionData();
        }
    }, [id, showToast]);

    const handleFeedbackChange = (field, value) => {
        setFeedbackData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveFeedback = async () => {
        try {
            setSaving(true);
            
            // Only send feedback fields to avoid overwriting other session data
            await updateSpeakingFeedback(id, {
                instructorFeedback: feedbackData.instructorFeedback,
                instructorAdvice: feedbackData.instructorAdvice
            });
            
            // Update local state
            setSessionData(prev => ({
                ...prev,
                instructorFeedback: feedbackData.instructorFeedback,
                instructorAdvice: feedbackData.instructorAdvice
            }));
            
            showToast('Feedback saved successfully!', 'success');
        } catch (error) {
            console.error('Failed to save feedback:', error);
            showToast('Failed to save feedback', 'error');
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatTime = (dateString) => {
        return new Date(dateString).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getSessionStatus = (session) => {
        if (session.isCompleted) {
            return { status: 'completed', color: 'success', text: 'Completed' };
        } else if (session.isReserved) {
            return { status: 'reserved', color: 'info', text: 'Reserved' };
        } else if (session.status === 'success') {
            return { status: 'success', color: 'primary', text: 'Active' };
        } else {
            return { status: 'pending', color: 'warning', text: 'Pending' };
        }
    };

    if (loading) {
        return (
            <Card sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 8, textAlign: 'center' }}>
                    <CircularProgress size={48} />
                    <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
                        Loading session data...
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    if (!sessionData) {
        return (
            <Card sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 8, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: 'text.primary' }}>
                        Session data not found
                    </Typography>
                </CardContent>
            </Card>
        );
    }

    const sessionStatus = getSessionStatus(sessionData);

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                        sx={{
                            backgroundColor: 'primary.main',
                            width: 48,
                            height: 48,
                            mr: 2,
                        }}
                    >
                        <FeedbackIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            Provide Speaking Feedback
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                            Review speaking session and provide detailed feedback and advice
                        </Typography>
                    </Box>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip
                            icon={<PersonIcon />}
                            label={`Instructor: ${sessionData.instructorId?.toString().substring(0, 8)}...`}
                            color="info"
                            variant="outlined"
                        />
                        <Chip
                            icon={<ScheduleIcon />}
                            label={`Scheduled: ${formatDate(sessionData.startsAt)}`}
                            color="primary"
                            variant="outlined"
                        />
                        <Chip
                            label={sessionStatus.text}
                            color={sessionStatus.color}
                            variant="outlined"
                        />
                    </Box>
                    
                    <ModernButton
                        onClick={handleSaveFeedback}
                        disabled={saving}
                        variant="contained"
                        startIcon={<EditIcon />}
                    >
                        {saving ? 'Saving...' : 'Save Feedback'}
                    </ModernButton>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Session Information */}
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 3, position: 'sticky', top: 100 }}>
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Avatar
                                    sx={{
                                        backgroundColor: 'info.main',
                                        width: 40,
                                        height: 40,
                                        mr: 2,
                                    }}
                                >
                                    <VideoCallIcon />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Session Details
                                </Typography>
                            </Box>
                            
                            {/* Session Info */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                                    Session Information
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Session ID:</strong> {sessionData._id?.toString().substring(0, 12)}...
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Instructor ID:</strong> {sessionData.instructorId?.toString().substring(0, 12)}...
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    <strong>Duration:</strong> {sessionData.sessionDuration} minutes
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Time Zone:</strong> {sessionData.timeZone}
                                </Typography>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Schedule */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                                    Schedule
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <TimeIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                                    <Typography variant="body2">
                                        <strong>Start:</strong> {formatTime(sessionData.startsAt)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <TimeIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                                    <Typography variant="body2">
                                        <strong>End:</strong> {formatTime(sessionData.endsAt)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 1 }} />
                                    <Typography variant="body2">
                                        <strong>Date:</strong> {formatDate(sessionData.startsAt)}
                                    </Typography>
                                </Box>
                            </Box>

                            <Divider sx={{ my: 2 }} />

                            {/* Meeting Links */}
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                                    Meeting Links
                                </Typography>
                                {sessionData.eventLink && (
                                    <Box sx={{ mb: 1 }}>
                                        <Link 
                                            href={sessionData.eventLink} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            sx={{ fontSize: '0.875rem' }}
                                        >
                                            Open Calendar Event
                                        </Link>
                                    </Box>
                                )}
                                {sessionData.meetLink && (
                                    <Box>
                                        <Link 
                                            href={sessionData.meetLink} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            sx={{ fontSize: '0.875rem' }}
                                        >
                                            Join Meeting
                                        </Link>
                                    </Box>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Feedback Form */}
                <Grid item xs={12} md={8}>
                    <Card sx={{ borderRadius: 3, mb: 3 }}>
                        <CardContent sx={{ p: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Avatar
                                    sx={{
                                        backgroundColor: 'primary.main',
                                        width: 40,
                                        height: 40,
                                        mr: 2,
                                    }}
                                >
                                    <LanguageIcon />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                    Speaking Session Feedback
                                </Typography>
                            </Box>

                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                                        Instructor Feedback
                                    </Typography>
                                    <ModernTextField
                                        label="Provide detailed feedback on the student's speaking performance"
                                        multiline
                                        rows={8}
                                        value={feedbackData.instructorFeedback}
                                        onChange={(e) => handleFeedbackChange('instructorFeedback', e.target.value)}
                                        placeholder="Enter your detailed feedback here... Consider aspects like pronunciation, fluency, vocabulary, grammar, coherence, and task achievement..."
                                        fullWidth
                                    />
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
                                        Instructor Advice
                                    </Typography>
                                    <ModernTextField
                                        label="Provide specific advice for improvement"
                                        multiline
                                        rows={8}
                                        value={feedbackData.instructorAdvice}
                                        onChange={(e) => handleFeedbackChange('instructorAdvice', e.target.value)}
                                        placeholder="Enter your advice here... Focus on specific areas for improvement, practice suggestions, and actionable recommendations..."
                                        fullWidth
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Current Feedback Display */}
                    {(sessionData.instructorFeedback || sessionData.instructorAdvice) && (
                        <Card sx={{ borderRadius: 3, mb: 3 }}>
                            <CardContent sx={{ p: 4 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, color: 'text.primary' }}>
                                    Current Feedback
                                </Typography>
                                
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                                            Current Instructor Feedback
                                        </Typography>
                                        <Paper sx={{ p: 2, backgroundColor: 'info.50', borderRadius: 1, minHeight: 120 }}>
                                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                                {sessionData.instructorFeedback || 'No feedback provided yet'}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                                            Current Instructor Advice
                                        </Typography>
                                        <Paper sx={{ p: 2, backgroundColor: 'success.50', borderRadius: 1, minHeight: 120 }}>
                                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                                                {sessionData.instructorAdvice || 'No advice provided yet'}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    )}

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <ModernButton
                            onClick={() => navigate('/speaking-feedback')}
                            variant="outlined"
                        >
                            Back to Sessions List
                        </ModernButton>
                        
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <ModernButton
                                onClick={handleSaveFeedback}
                                disabled={saving}
                                variant="contained"
                                startIcon={<EditIcon />}
                            >
                                {saving ? 'Saving...' : 'Save Feedback'}
                            </ModernButton>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SpeakingFeedbackForm;
